import { useEffect, useRef, useState } from "react";
import { authHeader } from "../auth/token.js";
import type { Eta } from "@veyso/core";

export interface ActiveJob {
  id: string;
  projectId: string;
  projectName: string | null;
  type: string;
  status: string;
  step: string | null;
  progress: number | null;
  label: string | null;
  eta: Eta | null;
  /** Whether finishing it emails the person looking: they own it, and it is a kind that emails. */
  emailsYou?: boolean;
}

export interface JobActivity {
  active: ActiveJob[];
  /** Changes whenever any job finishes, which is what marks a screen stale. */
  settledAt: string | null;
  settledType: string | null;
  settledStatus: string | null;
  settledProjectId: string | null;
}

const EMPTY: JobActivity = {
  active: [],
  settledAt: null,
  settledType: null,
  settledStatus: null,
  settledProjectId: null,
};

/** Brisk while something is running, unhurried when nothing is. */
const BUSY_MS = 1500;
const IDLE_MS = 6000;

/**
 * One poller for the whole app.
 *
 * Every screen wants the same two facts — what is running, and whether anything
 * has finished since it last looked — and a hook per screen would mean three
 * timers asking the same question out of step with each other. A module-level
 * singleton polls once and fans out; it starts when the first subscriber
 * arrives and stops when the last leaves, so a backgrounded tab with no
 * subscribers is not quietly polling for ever.
 */
let current: JobActivity = EMPTY;
let timer: ReturnType<typeof setTimeout> | undefined;
const subscribers = new Set<(value: JobActivity) => void>();

async function poll(): Promise<void> {
  try {
    // The poller does not go through `api.request`, so it carries the token
    // itself. Without it every tick is a 401 and the activity bar stays empty
    // while work is plainly running — a silent failure, since a failed poll is
    // deliberately not surfaced.
    const response = await fetch("/api/jobs/active", { headers: authHeader() });
    if (response.ok) {
      current = (await response.json()) as JobActivity;
      for (const notify of subscribers) notify(current);
    }
  } catch {
    // A failed poll is not worth surfacing: the next one is a second away, and
    // an error banner for a background refresh is noise about nothing.
  } finally {
    if (subscribers.size > 0) {
      timer = setTimeout(() => void poll(), current.active.length > 0 ? BUSY_MS : IDLE_MS);
    }
  }
}

function subscribe(notify: (value: JobActivity) => void): () => void {
  subscribers.add(notify);
  if (subscribers.size === 1) void poll();
  return () => {
    subscribers.delete(notify);
    if (subscribers.size === 0 && timer) {
      clearTimeout(timer);
      timer = undefined;
    }
  };
}

/**
 * Asks now rather than waiting for the next tick.
 *
 * The idle poll is six seconds, and a rebuild can start and finish inside that
 * — so pressing a button and seeing nothing in the bar was possible even though
 * the work had begun. Anything that queues a job calls this, and the bar picks
 * it up within a frame instead of within six seconds.
 */
export function nudgeJobActivity(): void {
  if (subscribers.size === 0) return;
  if (timer) clearTimeout(timer);
  void poll();
}

/** What is running right now, and what last finished. */
export function useJobActivity(): JobActivity {
  const [value, setValue] = useState<JobActivity>(current);
  useEffect(() => subscribe(setValue), []);
  return value;
}

/**
 * Runs `handler` whenever a job finishes anywhere.
 *
 * The first reading is ignored on purpose: a screen that has only just mounted
 * is already showing fresh data, and reloading because a job finished an hour
 * ago would make every navigation fetch twice.
 *
 * `projectId` narrows it to one project's work, which is what the editor wants —
 * a render finishing in another tab should not reload the script you are editing.
 */
export function useOnJobSettled(handler: () => void, projectId?: string): void {
  const seen = useRef<string | null>(null);
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(
    () =>
      subscribe((value) => {
        if (!value.settledAt) return;
        if (projectId && value.settledProjectId !== projectId) return;
        if (seen.current === null) {
          seen.current = value.settledAt;
          return;
        }
        if (seen.current !== value.settledAt) {
          seen.current = value.settledAt;
          latest.current();
        }
      }),
    [projectId],
  );
}

/** What the bar says while a job runs. Plain words, not our job type names. */
export const JOB_ACTIVITY_LABELS: Record<string, string> = {
  process: "Processing the recording",
  script: "Writing the script",
  voice: "Recording the narration",
  autocut: "Rebuilding the cut",
  proxy: "Preparing the preview",
  vision: "Looking at the recording",
  render: "Rendering the video",
  launch: "Making the launch video",
  translate: "Translating",
  language_voice: "Voicing the script",
};
